import { type PhysicsTuning, SIM_HZ } from '../config/tuning';
import { Surface } from '../track/types';
import { type CarState, createCar, placeCar, stepCar } from './car';
import { dcos, dsin } from './dmath';
import type { InputFrame } from './input';
import type { TrackRuntime } from './trackQuery';

/**
 * The whole simulation state: plain serializable data. The same spawn state +
 * the same per-tick input sequence always produces the same state, bit for bit.
 */
export interface LapTiming {
  /** True once the car has crossed the line (the run-up is not timed). */
  started: boolean;
  lapIndex: number;
  /** Fractional tick at which the current lap started. */
  lapStart: number;
  /** Next line expected: 1 = sector 1 line, 2 = sector 2 line, 3 = finish. */
  nextLine: number;
  sectorStart: number;
  /** Completed sector times of the current lap, in fractional ticks. */
  sectorTicks: number[];
  valid: boolean;
  /** Tick at which the current lap was invalidated (-1 = still valid). */
  invalidTick: number;
  prevS: number;
}

export interface SimState {
  /** Ticks since spawn. */
  tick: number;
  car: CarState;
  timing: LapTiming;
}

export type SimEvent =
  | { type: 'lapStart'; lapIndex: number; time: number }
  | { type: 'sector'; sector: number; ticks: number; ms: number; lapIndex: number }
  | {
      type: 'lapComplete';
      lapIndex: number;
      startTime: number;
      endTime: number;
      ticks: number;
      ms: number;
      sectorTicks: number[];
      sectorMs: number[];
      valid: boolean;
      /** Last tick of the lap (inputs up to and including this tick produced it). */
      endTick: number;
    }
  | { type: 'invalidated'; lapIndex: number; tick: number }
  | { type: 'barrier'; speed: number };

export function ticksToMs(ticks: number): number {
  return Math.round((ticks * 1000) / SIM_HZ);
}

export function spawnState(rt: TrackRuntime, p: PhysicsTuning): SimState {
  const car = createCar();
  const d = rt.data;
  placeCar(car, rt, p, rt.length - d.spawnDistance, d.spawnSpeed, d.spawnOffset ?? 0);
  return {
    tick: 0,
    car,
    timing: {
      started: false,
      lapIndex: 0,
      lapStart: 0,
      nextLine: 1,
      sectorStart: 0,
      sectorTicks: [],
      valid: true,
      invalidTick: -1,
      prevS: car.s,
    },
  };
}

const WRAP_WINDOW = 60;

export function stepWorld(
  state: SimState,
  input: InputFrame,
  rt: TrackRuntime,
  p: PhysicsTuning,
  events: SimEvent[] | null,
): void {
  const car = state.car;
  stepCar(car, input, rt, p);
  state.tick++;
  if (rt.hooks.length > 0) collideObstacles(state, rt, p);
  if (events && car.barrierImpact > 0) events.push({ type: 'barrier', speed: car.barrierImpact });

  const tm = state.timing;
  const L = rt.length;
  const prevS = tm.prevS;
  const currS = car.s;
  const tick = state.tick;

  if (prevS > L - WRAP_WINDOW && currS < WRAP_WINDOW) {
    // Forward over the start/finish line, at fraction f of this tick.
    const before = L - prevS;
    const f = before / (before + currS);
    const time = tick - 1 + f;
    if (tm.started && tm.nextLine === 3) {
      const sectorTicks = [tm.sectorTicks[0], tm.sectorTicks[1], time - tm.sectorStart];
      const ticks = time - tm.lapStart;
      events?.push({
        type: 'lapComplete',
        lapIndex: tm.lapIndex,
        startTime: tm.lapStart,
        endTime: time,
        ticks,
        ms: ticksToMs(ticks),
        sectorTicks,
        sectorMs: sectorTicks.map(ticksToMs),
        valid: tm.valid,
        endTick: tick,
      });
    }
    tm.started = true;
    tm.lapIndex++;
    tm.lapStart = time;
    tm.sectorStart = time;
    tm.nextLine = 1;
    tm.sectorTicks = [];
    tm.valid = true;
    tm.invalidTick = -1;
    events?.push({ type: 'lapStart', lapIndex: tm.lapIndex, time });
  } else if (prevS < WRAP_WINDOW && currS > L - WRAP_WINDOW) {
    // Backwards over the line: the lap in progress no longer counts.
    tm.started = false;
    tm.nextLine = 1;
  } else if (tm.started) {
    for (let k = 1; k <= 2; k++) {
      const lineS = rt.sectorS[k - 1];
      if (tm.nextLine === k && prevS < lineS && currS >= lineS) {
        const f = (lineS - prevS) / (currS - prevS);
        const time = tick - 1 + f;
        const ticks = time - tm.sectorStart;
        tm.sectorTicks.push(ticks);
        tm.sectorStart = time;
        tm.nextLine = k + 1;
        events?.push({ type: 'sector', sector: k - 1, ticks, ms: ticksToMs(ticks), lapIndex: tm.lapIndex });
      } else if (tm.nextLine === k + 1 && prevS >= lineS && currS < lineS) {
        // Reversed back over a sector line: it has to be crossed again.
        tm.nextLine = k;
        tm.sectorTicks.pop();
        let start = tm.lapStart;
        for (const t of tm.sectorTicks) start += t;
        tm.sectorStart = start;
      }
    }
  }

  // Track limits: all four wheels off asphalt and curbs.
  if (tm.started && tm.valid) {
    const sf = car.surfaces;
    if (sf[0] >= Surface.Grass && sf[1] >= Surface.Grass && sf[2] >= Surface.Grass && sf[3] >= Surface.Grass) {
      tm.valid = false;
      tm.invalidTick = tick;
      events?.push({ type: 'invalidated', lapIndex: tm.lapIndex, tick });
    }
  }
  tm.prevS = currS;
}

/**
 * Track-modifier obstacles, on the lap clock (ticks since the current lap
 * started), so every lap meets them in the same rhythm.
 */
function collideObstacles(state: SimState, rt: TrackRuntime, p: PhysicsTuning): void {
  const car = state.car;
  const L = rt.length;
  const lapTick = state.timing.started ? state.tick - Math.floor(state.timing.lapStart) : state.tick;
  for (const h of rt.hooks) {
    const list = h.obstacles?.(lapTick);
    if (!list) continue;
    for (const o of list) {
      let ds = car.s - o.s;
      if (ds > L * 0.5) ds -= L;
      else if (ds < -L * 0.5) ds += L;
      const dd = car.d - o.d;
      const dist = Math.sqrt(ds * ds + dd * dd);
      const minDist = o.r + p.carHalfWidth;
      if (dist >= minDist || dist < 1e-9) continue;
      const i = car.trackIndex;
      const tx = rt.tx[i];
      const ty = rt.ty[i];
      // Outward normal of the obstacle, in world space.
      const nx = (tx * ds - ty * dd) / dist;
      const ny = (ty * ds + tx * dd) / dist;
      const pen = minDist - dist;
      car.x += nx * pen;
      car.y += ny * pen;
      const ch = dcos(car.heading);
      const sh = dsin(car.heading);
      let Vx = car.vx * ch - car.vy * sh;
      let Vy = car.vx * sh + car.vy * ch;
      const vn = Vx * nx + Vy * ny;
      if (vn < 0) {
        const e = p.barrierRestitution;
        Vx -= (1 + e) * vn * nx;
        Vy -= (1 + e) * vn * ny;
        car.vx = Vx * ch + Vy * sh;
        car.vy = -Vx * sh + Vy * ch;
        car.yawRate *= 0.6;
        car.barrierImpact = -vn;
      }
    }
  }
}

/** Deep copy (the state is plain data). */
export function cloneState(s: SimState): SimState {
  return {
    tick: s.tick,
    car: { ...s.car, surfaces: s.car.surfaces.slice() },
    timing: { ...s.timing, sectorTicks: s.timing.sectorTicks.slice() },
  };
}
